// Pure scorecard timeline builders, extracted from ScorecardClient.tsx
// (2026-09-11). The mobile app ports these 1:1 in
// lib/features/matches/domain/scorecard_timeline.dart and both are pinned by
// test/fixtures/scorecard/*.json. Change behaviour here → regenerate the
// fixtures → update the Dart port in the same change.
//
// Live games are resolved by ./resolve.ts (toScorecardGames); this module
// only builds display rows.

export type Side = 'player_1' | 'player_2';
export type ConductLevel = 'warning' | 'conduct_stroke' | 'conduct_game' | 'conduct_match';

export interface ScorecardPoint {
  id: string;
  /** Present on every stored row and every resolved point; drives ordering when present on all points. */
  order_number?: number;
  player_1_score: number;
  player_2_score: number;
  serving_player: Side | null;
  point_type: string | null;
  created_at: string;
  metadata: unknown;
}

export interface TimelineAction {
  id: string;
  type: 'point' | 'let_call' | 'conduct';
  metadata?: { isNoLet?: boolean; isStroke?: boolean; conductLevel?: ConductLevel };
  player1Score: number;
  player2Score: number;
  servingPlayer: Side;
  serviceBoxSide: 'L' | 'R';
  isWinningPoint: boolean;
  timestamp: string;
  scoringPlayer?: Side | null;
}

export interface ReconstructedGame {
  id: string;
  game_number: number;
  player_1_score: number;
  player_2_score: number;
  match_game_point: ScorecardPoint[];
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';

/** Port of GameScoreTable.createTimelineActions — most recent first.
 * Points are ordered by order_number when every point carries one (stored rows all do; pre-2026-09-12 rows share one created_at per save, so created_at alone cannot order them), else by created_at.
 */
export function createTimelineActions(
  points: ScorecardPoint[],
  pointsPerGame: number,
): TimelineAction[] {
  const timelineActions: TimelineAction[] = [];
  const allOrdered = points.length > 0 && points.every((p) => typeof p.order_number === 'number');
  const sortedPoints = [...points].sort((a, b) =>
    allOrdered
      ? (a.order_number as number) - (b.order_number as number)
      : new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  sortedPoints.forEach((point) => {
    const md = isObj(point.metadata) ? point.metadata : null;
    const serviceBoxSide: 'L' | 'R' =
      md && 'serviceBoxSide' in md ? (md.serviceBoxSide === 'left' ? 'L' : 'R') : 'R';
    const isWinningPoint =
      point.player_1_score === pointsPerGame || point.player_2_score === pointsPerGame;
    const servingPlayer = point.serving_player as Side;

    const isConduct = point.point_type === 'conduct' || (md !== null && 'conductLevel' in md);
    if (isConduct) {
      const prev = timelineActions[timelineActions.length - 1];
      const conductLevel =
        md && 'conductLevel' in md ? (md.conductLevel as ConductLevel) : undefined;
      timelineActions.push({
        id: point.id,
        type: 'conduct',
        metadata: { conductLevel },
        player1Score: prev ? prev.player1Score : 0,
        player2Score: prev ? prev.player2Score : 0,
        servingPlayer,
        serviceBoxSide,
        isWinningPoint: false,
        timestamp: point.created_at,
        scoringPlayer: servingPlayer,
      });
      return;
    }

    const isNoLet = !!(md && 'isNoLet' in md && md.isNoLet);
    const isStroke = !!(md && 'isStroke' in md && md.isStroke);

    const prev = timelineActions[timelineActions.length - 1];
    const prevPlayer1Score = prev ? prev.player1Score : 0;
    const prevPlayer2Score = prev ? prev.player2Score : 0;

    let scoringPlayer: Side | null = null;
    if (point.point_type === 'point') {
      if (point.player_1_score > prevPlayer1Score) scoringPlayer = 'player_1';
      else if (point.player_2_score > prevPlayer2Score) scoringPlayer = 'player_2';
    }

    if (isNoLet) {
      timelineActions.push({
        id: `${point.id}_nolet_display`,
        type: 'point',
        metadata: { isNoLet: true },
        player1Score: prevPlayer1Score,
        player2Score: prevPlayer2Score,
        servingPlayer,
        serviceBoxSide,
        isWinningPoint: false,
        timestamp: point.created_at,
      });
    }
    if (isStroke) {
      timelineActions.push({
        id: `${point.id}_stroke_display`,
        type: 'point',
        metadata: { isStroke: true },
        player1Score: prevPlayer1Score,
        player2Score: prevPlayer2Score,
        servingPlayer,
        serviceBoxSide,
        isWinningPoint: false,
        timestamp: point.created_at,
      });
    }

    timelineActions.push({
      id: point.id,
      type: point.point_type as 'point' | 'let_call',
      player1Score: point.player_1_score,
      player2Score: point.player_2_score,
      servingPlayer,
      serviceBoxSide,
      isWinningPoint,
      timestamp: point.created_at,
      scoringPlayer,
    });
  });

  return timelineActions.reverse();
}
